import { useEffect, useLayoutEffect, useState } from 'react';
import { initServices, Services } from '@packages/shared/core/common/context';
import { createStore } from '@packages/shared/core/common/store';
import {
  FeatureFlagsConfigLoadedEvent,
  FeatureFlagsConfigLoadingFailedEvent,
} from '@packages/shared/core/feature-flags/events';
import { fetchLocationRequest } from '@packages/shared/core/location/store';
import { config } from '@packages/shared/config';
import { LoadingStatuses } from '../entities';
import { useFeatureFlagsLoader } from './useFeatureFlagsLoader';
import { useFeatureFlagsListeners } from './useFeatureFlagsListeners';

export const useApp = () => {
  const [featureFlagsLoadingStatus, setFeatureFlagsLoadingStatus] = useState(LoadingStatuses.PENDING);
  const [appServices] = useState<Services>(() => initServices(config));
  const [appStore] = useState<ReturnType<typeof createStore>>(createStore(appServices));
  const {
    featureFlags,
    analyticsService,
    logger,
    clarityHeatmapRecordingService,
    cookieConsentService,
    errorMonitoringService,
  } = appServices;

  const { isLoaderVisible, hideLoader } = useFeatureFlagsLoader(logger);

  useEffect(() => {
    cookieConsentService.initialize();
  }, [cookieConsentService]);

  useFeatureFlagsListeners(featureFlags, {
    onLoaded: (features) => {
      hideLoader();
      setFeatureFlagsLoadingStatus(LoadingStatuses.FULFILLED);
      analyticsService.track(new FeatureFlagsConfigLoadedEvent({ config: features }));
    },
    onFailed: (error) => {
      hideLoader();
      setFeatureFlagsLoadingStatus(LoadingStatuses.FAILED);
      logger.error(error);
      analyticsService.track(new FeatureFlagsConfigLoadingFailedEvent({ error }));
    },
  });

  useLayoutEffect(() => {
    appServices.analyticsService.launch();
  }, [appServices]);

  useLayoutEffect(() => {
    const identifyUser = async () => {
      try {
        const deviceId = await analyticsService.getDeviceId();
        errorMonitoringService.identify(deviceId);
      } catch {}
    };

    identifyUser();
  }, [analyticsService, errorMonitoringService]);

  useEffect(() => {
    appStore.dispatch(fetchLocationRequest());
  }, [appStore]);

  useEffect(() => {
    clarityHeatmapRecordingService.initialize();
  }, [clarityHeatmapRecordingService]);

  useEffect(() => {
    featureFlags.onAttributesUpdated((attributes) => {
      appServices.analyticsService.setFeatureFlagsAttributes(attributes);

      if (attributes.id) {
        appServices.analyticsService.addUserAssociatedId(attributes.id);
      }
    });
  }, [appServices, featureFlags]);

  return {
    isLoaderVisible,
    store: appStore,
    services: appServices,
    featureFlags,
    featureFlagsLoadingStatus,
  };
};
